import type {
  VotingQuestion,
  VotingQuestionWithResults,
} from '../api/DigiobcanApi';
import { __ } from '../utils';
import { shortId } from './ids';
import type { StatusChipTone } from '@presov.sk/presov-ui-react';

/**
 * Spoločné veci správy hlasovaní - to, čo potrebuje zoznam aj detail,
 * administrácia aj občianska strana.
 */

/**
 * Sentinel v adrese, ktorým sa `VotingDetailAdminPage` prepína do režimu
 * „nové hlasovanie" - obrazovka vytvorenia nie je vlastná routa.
 *
 * Porovnávaj ho **na surovom parametri z `useParams`**, nikdy nie na výsledku
 * `entityId` - prečo, je v jej dokumentácii v [[ids]].
 */
export const NEW_VOTING_PARAM = 'nove';

/** Cesta na obrazovku vytvorenia hlasovania. */
export const NEW_VOTING_PATH = `/administracia/hlasovania/${NEW_VOTING_PARAM}`;

/**
 * Sústredený režim hlasovania - jedna otázka na obrazovku, mimo shellu.
 *
 * Je to **vlastná adresa**, nie stav detailu: z detailu sa doň vchádza
 * tlačidlom, prerušenie je krok späť a rozpísaný lístok tak neprežíva
 * preklikanie appky. Shell ju pozná podľa `isVotingRunPath` a nekreslí sa nad
 * ňou - to je to „prekryje aplikáciu" z návrhu.
 */
export const votingRunPath = (votingId: string) =>
  `/hlasovania/${shortId(votingId)}/hlasovat`;

/** Výsledky hlasovania pre občana. */
export const votingResultsPath = (votingId: string) =>
  `/hlasovania/${shortId(votingId)}/vysledky`;

/** Detail (úvod) hlasovania pre občana. */
export const votingDetailPath = (votingId: string) =>
  `/hlasovania/${shortId(votingId)}`;

/**
 * Beží na tejto adrese sústredený režim? Pýta sa na to shell, takže to musí byť
 * čistá funkcia nad `pathname`, nie stav v `LayoutProvider`-i: ten sa plní
 * v efekte, takže by shell na prvý render ešte stihol nakresliť hlavičku.
 */
export const isVotingRunPath = (pathname: string) =>
  /^\/hlasovania\/[^/]+\/hlasovat\/?$/.test(pathname);

/**
 * Hranice škály. Rovnaké čísla drží `CHECK` v migrácii `000019` aj OpenAPI,
 * takže hodnota mimo nich neprejde ani vtedy, keď sa obíde formulár.
 */
export const MIN_SCALE_MAX = 2;
export const MAX_SCALE_MAX = 10;
export const DEFAULT_SCALE_MAX = 5;

/**
 * Dolná hranica škály. Smie byť 0 alebo 1 - drží to `CHECK` v migrácii 000021
 * aj OpenAPI, rovnako ako pri `scale_max`.
 *
 * `DEFAULT_SCALE_MIN` je zároveň fallback pre otázky, ktoré `scale_min` nemajú
 * zapísaný (vznikli pred 000021). Tá istá derivácia je v `submitVote`
 * v digiobcan-api; keď sa rozídu, appka ponúkne inú množinu hodnôt, než akú
 * server prijme.
 */
export const MIN_SCALE_MIN = 0;
export const MAX_SCALE_MIN = 1;
export const DEFAULT_SCALE_MIN = 1;

/**
 * Body škály, teda čo sa hlasujúcemu nakreslí ako tlačidlá a čo tvorí os
 * výsledkov. Jediné miesto, kde sa `scale_min`/`scale_max` prekladá na zoznam -
 * kým to bolo napísané v každom rendereri zvlášť, znamenala nová hranica
 * off-by-one v tom, na ktorý sa zabudlo.
 */
export const scalePoints = (question: {
  scale_min?: number | null;
  scale_max?: number | null;
}): number[] => {
  const min = question.scale_min ?? DEFAULT_SCALE_MIN;
  const max = question.scale_max ?? DEFAULT_SCALE_MAX;
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
};

/**
 * Popis hlasovania ako **obyčajný text**.
 *
 * Popis je markdown a detail ho tak aj kreslí (`MarkdownPreview`), lenže
 * v zoznamoch je z neho výňatok v jedinom `Typography` - v karte skrátený na
 * dva riadky a s celým textom v bubline. Tam sa markdown vykresliť nedá:
 * skracovanie na dva riadky reže riadkový obsah, nie blokové elementy, ktoré
 * by z markdownu vznikli. Bez tohto prevodu preto v zozname svietila samotná
 * syntax - `**dôležité**`, `##` aj `[text](url)`.
 *
 * To isté potrebuje `og:description` na detaile; meta tag nemá čo vykresliť.
 */
export const plainDescription = (description?: string | null): string =>
  (description ?? '')
    // HTML aj obrázky: z obrázka v texte nezostane nič na prečítanie.
    .replace(/<[^>]*>/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    // Z odkazu zostáva jeho text, nie URL.
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    // Vodorovná linka celá, potom značky na začiatku riadku (nadpis, citácia,
    // odrážka, číslovanie) a nakoniec zvýraznenia, kód a preškrtnutie.
    .replace(/^\s*([-*_]\s*){3,}$/gm, ' ')
    .replace(/^\s{0,3}(#{1,6}|>+|[-*+]|\d+\.)\s+/gm, '')
    .replace(/[*_`~]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/** Stav hlasovania odvodený z dvojice dátumov. */
export type VotingState = 'archived' | 'draft' | 'open' | 'closed';

type VotingDates = {
  open_from?: Date | string | null;
  open_to?: Date | string | null;
  forgotten?: boolean;
};

const asDate = (value: Date | string | null | undefined): Date | null =>
  value ? new Date(value) : null;

/**
 * Stav hlasovania nie je v dátach, počíta sa z dvojice dátumov a `forgotten`.
 * Zámerne tu, nie v každej stránke zvlášť: to isté odvodenie potrebuje
 * administrácia aj občiansky zoznam a rozídené by znamenalo, že tá istá
 * položka má na dvoch obrazovkách iný stav.
 */
export const votingState = (voting: VotingDates, now: Date): VotingState => {
  if (voting.forgotten) return 'archived';

  const openFrom = asDate(voting.open_from);
  if (!openFrom || openFrom > now) return 'draft';

  const openTo = asDate(voting.open_to);
  if (openTo && openTo <= now) return 'closed';

  return 'open';
};

/**
 * Popis stavu pre administráciu. Farba je tichý tint (`StatusChip`), nie ikona
 * s farbou: v riadku je stav jediná farebná plocha, takže nesie význam sám.
 */
export const adminVotingStatus = (
  voting: VotingDates,
  now: Date,
): { label: string; tone: StatusChipTone } => {
  switch (votingState(voting, now)) {
    case 'archived':
      return { label: __('Archivované'), tone: 'neutral' };
    case 'draft':
      return { label: __('Draft'), tone: 'warning' };
    case 'closed':
      return { label: __('Uzavreté'), tone: 'success' };
    case 'open':
      return { label: __('Otvorené'), tone: 'info' };
  }
};

/**
 * Popis stavu pre občana. Iné slová než v administrácii zámerne - „Draft" nič
 * nepovie tomu, kto hlasovanie len číta.
 *
 * Vetva `archived` tu nie je a nemá byť: archivované (zabudnuté) hlasovanie
 * z API nepríde, občianske dotazy ho filtrujú cez `secret IS NOT NULL`. Keby
 * ju tu niekto „doplnil", vytvoril by dojem, že taký stav občan vidieť môže.
 */
export const citizenVotingStatus = (
  voting: VotingDates,
  now: Date,
): { label: string; tone: StatusChipTone } => {
  switch (votingState(voting, now)) {
    case 'draft':
      return { label: __('Pripravované'), tone: 'warning' };
    case 'closed':
      return { label: __('Ukončené'), tone: 'neutral' };
    default:
      return { label: __('Prebiehajúce'), tone: 'info' };
  }
};

/**
 * Zverejňuje hlasovanie výsledky občanovi **vôbec**?
 *
 * Odpoveď staršieho API `available_results` nenesie; chýbajúca hodnota preto
 * znamená „áno", teda správanie spred migrácie 000022.
 */
export const votingResultsPublished = (voting: {
  available_results?: boolean;
}): boolean => voting.available_results !== false;

/**
 * Vydá server tomuto občanovi podrobné výsledky **teraz**?
 *
 * Je to **presne `shouldIncludeQuestions` z `getVotingResults`** prepísané do
 * appky, a preto to stojí tu, nie v troch stránkach zvlášť: odkaz na výsledky
 * kreslia zoznam, detail aj sústredený režim, a keď sa tá podmienka rozíde
 * so serverom, odkaz vedie na prázdnu stránku.
 *
 *  * `available_results` je **či** - vypnuté znamená, že sa nezverejnia ani po
 *    uzavretí. Chýbajúca hodnota (staršia odpoveď API) je `true`.
 *  * `available_incomplete_results` je **kedy** - bez neho až po uzavretí.
 *
 * **Platí to len pre občana.** Správca hlasovaní dostane výsledky vždy a jeho
 * obrazovky sa preto na túto funkciu nepýtajú ([[VotingLockedView]]).
 */
export const citizenResultsVisible = (
  voting: VotingDates & {
    available_results?: boolean;
    available_incomplete_results?: boolean;
  },
  now: Date,
): boolean => {
  if (!votingResultsPublished(voting)) return false;

  const state = votingState(voting, now);
  if (state === 'archived' || state === 'draft') return false;

  return state === 'closed' || !!voting.available_incomplete_results;
};

/**
 * Typ otázky. Je to **ten istý enum ako na drôte** - kým bol UI-only
 * (`'options' | 'free_text'`), musel sa pri načítaní dopočítavať oňuchávaním
 * možností a pri renderovaní druhýkrát z `min/max`. Teraz ho nesie API.
 *
 * Býval vo `VotingDetailAdminPage`, ale odkedy typ nesie aj občianska strana
 * (dlaždice v builderi, súhrn otázok na úvode, hlavička výsledkov), patrí sem:
 * inak by ho stránka pre občana musela importovať z administrácie.
 */
export type QuestionType = VotingQuestion['type'];

/** Poradie, v akom typy stoja v builderi (4b aj dlaždice v editore). */
export const QUESTION_TYPES: QuestionType[] = [
  'single_choice',
  'multi_choice',
  'free_text',
  'scale',
];

/** Meno typu otázky. Jedno miesto - inak sa v builderi a vo výsledkoch rozíde. */
export const questionTypeLabel = (type: QuestionType): string => {
  switch (type) {
    case 'single_choice':
      return __('Jedna možnosť');
    case 'multi_choice':
      return __('Viac možností');
    case 'free_text':
      return __('Otvorená odpoveď');
    case 'scale':
      return __('Škála');
  }
};

/** Veta pod menom typu na dlaždici (4b). */
export const questionTypeHint = (type: QuestionType): string => {
  switch (type) {
    case 'single_choice':
      return __('Občan vyberie jednu');
    case 'multi_choice':
      return __('S limitom min - max');
    case 'free_text':
      return __('Vlastnými slovami');
    case 'scale':
      return __('Hodnotenie tvrdení');
  }
};

type QuestionShape = {
  type: QuestionType;
  min_options: number;
  max_options: number;
  scale_min?: number;
  scale_max?: number;
  options: { type: 'choice' | 'free'; required?: boolean }[];
};

/**
 * Druhý riadok otázky v ľavom paneli buildera a v súhrne obsahu -
 * „Viac možností · vyber 1 - 3", „Škála 1 - 5 · 3 tvrdenia".
 *
 * Pri škále sa **nesmie** siahnuť na `min/max`: server ich prepisuje na počet
 * tvrdení, takže by z nich vyšlo „vyber 3 možnosti" pri otázke, kde sa nič
 * nevyberá.
 */
export const questionSummary = (question: QuestionShape): string => {
  const name = questionTypeLabel(question.type);

  if (question.type === 'scale') {
    const count = question.options.length;
    const points = scalePoints(question);
    const optional = question.options.filter(
      (option) => option.required === false,
    ).length;
    const statements = `${count} ${declineStatements(count)}`;
    return `${__('Škála')} ${points[0]} - ${points[points.length - 1]} · ${
      optional ? `${statements} (${optional} ${__('nepovinných')})` : statements
    }`;
  }

  if (question.type === 'free_text') {
    return `${name} · ${__('nepovinná')}`;
  }

  const count = question.options.length;

  if (question.type === 'single_choice') {
    return `${name} · ${count} ${declineAnswers(count)}`;
  }

  return question.min_options === question.max_options
    ? `${name} · ${__('vyber')} ${question.max_options}`
    : `${name} · ${__('vyber')} ${question.min_options} - ${question.max_options}`;
};

/**
 * Pravidlo otázky tak, ako ho číta občan - pilulka vedľa „Otázka 2 z 3".
 * Je to to isté pravidlo, ktoré rozhoduje o `canContinue` v sústredenom
 * režime; keby sa rozišli, pätička by zamkla krok bez toho, aby povedala prečo.
 */
export const answerRuleHint = (question: QuestionShape): string => {
  if (question.type === 'scale') {
    const required = question.options.filter(
      (option) => option.required !== false,
    ).length;
    if (!required) return __('Ohodnoťte, čo vás zaujíma');
    return required === question.options.length
      ? __('Ohodnoťte všetky tvrdenia')
      : __('Ohodnoťte aspoň povinné tvrdenia');
  }
  if (question.type === 'free_text') return __('Vlastnými slovami');
  if (question.type === 'single_choice') return __('Vyberte jednu možnosť');

  return question.min_options === question.max_options
    ? `${__('Vyberte')} ${question.max_options} ${declineOptions(question.max_options)}`
    : `${__('Vyberte')} ${question.min_options} - ${question.max_options} ${declineOptions(question.max_options)}`;
};

const declineOptions = (count: number) =>
  count === 1 ? __('možnosť') : count < 5 ? __('možnosti') : __('možností');

const declineAnswers = (count: number) =>
  count === 1 ? __('odpoveď') : count < 5 ? __('odpovede') : __('odpovedí');

const declineStatements = (count: number) =>
  count === 1 ? __('tvrdenie') : count < 5 ? __('tvrdenia') : __('tvrdení');

export { declineOptions, declineAnswers, declineStatements };

/**
 * Percento z podielu. Menovateľ nie je pri každom type ten istý:
 *
 * - jedna možnosť: súčet hlasov za možnosti (každý hlasujúci vybral práve raz),
 * - viac možností: **počet hlasujúcich**, nie súčet označení - inak by sa
 *   percentá sčítali na tristo a otázka „koľko ľudí chce zeleň" by nemala
 *   odpoveď.
 */
export const resultsDenominator = (
  question: VotingQuestionWithResults,
  votesCount: number,
): number => {
  if (question.type === 'multi_choice') return votesCount;
  const sum = question.options.reduce((acc, o) => acc + o.votes_count, 0);
  return sum || votesCount;
};

/** Počet označení naprieč možnosťami - hlavička otázky vo výsledkoch. */
export const totalMarks = (question: VotingQuestionWithResults): number =>
  question.options.reduce((acc, o) => acc + o.votes_count, 0);

const csvCell = (value: string | number | undefined | null): string => {
  const text = value === undefined || value === null ? '' : String(value);
  return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Výsledky do CSV pre podklad komisii (4e, 6a).
 *
 * Skladá sa **na klientovi** z toho, čo už stránka má - `getVotingResults`
 * vracia počty, `text_answers`, `average` aj `histogram`, takže na export
 * netreba nový endpoint. Oddeľovač je bodkočiarka a súbor začína BOM, lebo
 * cieľom je Excel v slovenskom locale; s čiarkou a bez BOM z neho urobí jeden
 * stĺpec s rozsypanou diakritikou.
 *
 * Sú to dva bloky za sebou, nie jedna tabuľka: agregát má číselné stĺpce,
 * otvorené odpovede text. V jednej tabuľke by museli byť polovice riadkov
 * prázdne.
 */
export const votingResultsToCsv = (
  voting: { title: string; votes_count: number },
  questions: VotingQuestionWithResults[],
): string => {
  const lines: string[] = [];

  lines.push(
    [__('Hlasovanie'), voting.title].map(csvCell).join(';'),
    [__('Počet hlasov'), voting.votes_count].map(csvCell).join(';'),
    '',
    [
      __('Otázka'),
      __('Typ'),
      __('Odpoveď'),
      __('Počet'),
      __('Podiel'),
      __('Priemer'),
    ]
      .map(csvCell)
      .join(';'),
  );

  questions.forEach((question, index) => {
    const label = `${index + 1}. ${question.title}`;
    const type = questionTypeLabel(question.type);

    if (question.type === 'scale') {
      question.options.forEach((option) => {
        const row = [
          label,
          type,
          option.title,
          option.histogram?.reduce((acc, b) => acc + b.count, 0) ?? 0,
          '',
          // `null` znamená „nikto neohodnotil", nie nulu.
          option.average === null || option.average === undefined
            ? ''
            : option.average.toFixed(2).replace('.', ','),
        ];
        lines.push(row.map(csvCell).join(';'));

        (option.histogram ?? []).forEach((bucket) => {
          lines.push(
            [
              label,
              type,
              `${option.title} - ${__('hodnotenie')} ${bucket.value}`,
              bucket.count,
              '',
              '',
            ]
              .map(csvCell)
              .join(';'),
          );
        });
      });
      return;
    }

    const denominator = resultsDenominator(question, voting.votes_count);

    question.options.forEach((option) => {
      const share = denominator
        ? `${((option.votes_count / denominator) * 100).toFixed(1).replace('.', ',')} %`
        : '';
      lines.push(
        [
          label,
          type,
          option.type === 'free' ? __('Iné (otvorená odpoveď)') : option.title,
          option.votes_count,
          share,
          '',
        ]
          .map(csvCell)
          .join(';'),
      );
    });
  });

  const textRows = questions.flatMap((question, index) =>
    question.options.flatMap((option) =>
      (option.text_answers ?? []).map((answer) => [
        `${index + 1}. ${question.title}`,
        answer,
      ]),
    ),
  );

  if (textRows.length) {
    lines.push(
      '',
      [__('Otázka'), __('Otvorená odpoveď')].map(csvCell).join(';'),
      ...textRows.map((row) => row.map(csvCell).join(';')),
    );
  }

  // BOM ako escape, nie ako znak v zdrojáku - literálny U+FEFF je pre
  // `no-irregular-whitespace` chyba a v diffe ho nikto neuvidí.
  return `\uFEFF${lines.join('\r\n')}\r\n`;
};

/** Stiahnutie CSV bez servera - `Blob` a odkaz, ktorý sa hneď zahodí. */
export const downloadCsv = (fileName: string, content: string) => {
  const url = URL.createObjectURL(
    new Blob([content], { type: 'text/csv;charset=utf-8' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/**
 * Limit otvorenej odpovede. Je to **klientský limit** - server žiadny
 * nedeklaruje, takže je zámerne prísnejší než čokoľvek, čo by prijal: pole bez
 * počítadla je pozvánka napísať esej, ktorú potom nemá kto prečítať.
 */
export const FREE_TEXT_MAX_LENGTH = 500;

/**
 * Jedna odpoveď na jednu možnosť. Tvar kopíruje telo `submitVote`, takže
 * odovzdaný lístok (`my_votes`) sa doň prekladá jediným premenovaním
 * `option_id` → `id`.
 */
export type AnswerOption = {
  id: number;
  questionId: number;
  text?: string;
  /** Hodnotenie tvrdenia na škále (1..scale_max). Len pri `type: 'scale'`. */
  value?: number;
};

/** Odpovede celého lístka, indexované `question_id`. */
export type VotingAnswers = Record<number, AnswerOption[]>;

/**
 * Otázka v tvare, v akom ju vidia funkcie nad odpoveďami. Nie je to
 * `QuestionShape & { … }`: prienik dvoch typov poľa nezúži typ prvku, takže
 * `options` by ostalo bez `option_id`.
 */
type AnsweredShape = {
  question_id: number;
  type: QuestionType;
  min_options: number;
  max_options: number;
  scale_min?: number;
  scale_max?: number;
  options: { option_id: number; type: 'choice' | 'free'; required?: boolean }[];
};

/**
 * Je otázka hotová podľa vlastného pravidla?
 *
 * Jediné miesto, kde toto pravidlo žije - pýta sa naň pätička sústredeného
 * režimu (odomknutie „Ďalej"), kontrola pred odoslaním (zelená vs. žltá karta)
 * aj odoslanie. Kým bolo napísané trikrát, dalo sa prejsť krokom, ktorý
 * kontrola vzápätí označila za neúplný.
 *
 * Otvorená odpoveď je **nepovinná** - preskočiť sa dá a prázdna sa neposiela.
 */
export const isQuestionAnswered = (
  question: AnsweredShape,
  answers: AnswerOption[] | undefined,
): boolean => {
  const value = answers ?? [];

  if (question.type === 'free_text') return true;

  // Škála je hotová, keď je ohodnotené každé **povinné** tvrdenie. Nepovinné
  // sa smie vynechať a `toVotesPayload` ho potom neposiela; to isté pravidlo
  // drží `submitVote` v digiobcan-api (`MISSING_REQUIRED_STATEMENTS`).
  // `required === undefined` znamená povinné - tak sa číta aj NULL v databáze.
  if (question.type === 'scale') {
    const rated = new Set(
      value
        .filter((answer) => typeof answer.value === 'number')
        .map((answer) => answer.id),
    );
    return question.options
      .filter((option) => option.required !== false)
      .every((option) => rated.has(option.option_id));
  }

  // Označená možnosť „Iné" bez textu je nedokončená odpoveď, nie odpoveď.
  const freeWithoutText = value.some((answer) => {
    const option = question.options.find(
      (candidate) => candidate.option_id === answer.id,
    );
    return option?.type === 'free' && !answer.text?.trim();
  });
  if (freeWithoutText) return false;

  if (question.type === 'single_choice') return value.length === 1;

  return (
    value.length >= question.min_options && value.length <= question.max_options
  );
};

/**
 * Odpoveď na otázku ako riadky textu - kontrola pred odoslaním (3c), lístok po
 * odoslaní (2e) aj to, čo bolo vyplnené v zavretom hlasovaní (8b).
 *
 * Vracia aj to, čo vyplnené **nie je** (`filled: false`), lebo práve to má
 * kontrola ukázať; skryť nevyplnené by z nej urobilo zoznam toho, čo je
 * v poriadku.
 */
/**
 * Od koľkých tvrdení sa škála v súhrne **zoskupuje podľa hodnotenia** namiesto
 * výpisu tvrdenie za tvrdením.
 *
 * Sedem je hranica z návrhu (`design_handoff_kontrola_pred_odoslanim`): kratší
 * zoznam sa prečíta ako mriežka „hodnota - tvrdenie", pri dlhšom (hlasovanie
 * o projektoch má v jednej otázke aj dvadsaťšesť tvrdení) z toho je stena
 * textu, v ktorej sa vlastná odpoveď nedá nájsť.
 */
export const SCALE_GROUPED_FROM = 7;

/**
 * Slovný význam hodnoty na škále - nadpis skupiny v súhrne („Najvyššia
 * priorita · 2 tvrdenia").
 *
 * Slová má len okraj škály a zadáva ich úradník (`scale_min_label`,
 * `scale_max_label`); vnútorné hodnoty ich nemajú a vypisujú sa číslom. Typ
 * je vlastný a nie `VotingQuestion` zámerne: tie dve polia pribudli do specu
 * neskoro, takže cez štruktúrny typ prejde aj otázka z generovaného klienta,
 * ktorý ich ešte nemá.
 */
export const scaleValueLabel = (
  question: {
    scale_min?: number | null;
    scale_max?: number | null;
    scale_min_label?: string;
    scale_max_label?: string;
  },
  value: number,
): string => {
  const points = scalePoints(question);
  const min = points[0];
  const max = points[points.length - 1];

  if (value === max && question.scale_max_label)
    return question.scale_max_label;
  if (value === min && question.scale_min_label)
    return question.scale_min_label;
  return `${__('Hodnotenie')} ${value}`;
};

type ScaleGroupsShape = {
  scale_min?: number | null;
  scale_max?: number | null;
  options: { option_id: number; title: string; required?: boolean }[];
};

/**
 * Odpoveď na škálu **zoskupená podľa hodnotenia**, od najvyššej hodnoty dole.
 *
 * Je to jadro redizajnu kontroly pred odoslaním: hlasujúci nechce čítať
 * dvadsaťšesť viet v tvare „E0101 Názov - nehodnotené", chce vidieť, čo dal
 * na prvé miesto. Preto sa vracajú **len skupiny, v ktorých niečo je**,
 * a nehodnotené tvrdenia stoja zvlášť - obrazovka ich nevypisuje, iba zhrnie
 * do jednej vety s možnosťou rozbaliť.
 *
 * `unrated` nesie aj `required`, lebo nehodnotené **povinné** tvrdenie nie je
 * to isté ako vynechané nepovinné: prvé je chyba, druhé je voľba.
 */
export const scaleAnswerGroups = (
  question: ScaleGroupsShape,
  answers: AnswerOption[] | undefined,
): {
  groups: { value: number; statements: string[] }[];
  unrated: { title: string; required: boolean }[];
  highest: number;
} => {
  const rated = new Map<number, number>();
  (answers ?? []).forEach((answer) => {
    if (typeof answer.value === 'number') rated.set(answer.id, answer.value);
  });

  const points = scalePoints(question);
  const groups = [...points]
    .reverse()
    .map((value) => ({
      value,
      statements: question.options
        .filter((option) => rated.get(option.option_id) === value)
        .map((option) => option.title),
    }))
    .filter((group) => group.statements.length > 0);

  const unrated = question.options
    .filter((option) => !rated.has(option.option_id))
    .map((option) => ({
      title: option.title,
      required: option.required !== false,
    }));

  return { groups, unrated, highest: points[points.length - 1] };
};

/**
 * Meta riadok karty v kontrole pred odoslaním - „Otázka 4 · škála 0 - 3 ·
 * ohodnotené 4 z 26 tvrdení".
 *
 * Hovorí o **odpovedi**, nie o otázke; `questionSummary` je ten druhý prípad
 * (čo otázka žiada) a používa ho builder aj úvod hlasovania. Keď odpoveď
 * nesplní pravidlo otázky, veta menuje presne to, čo chýba - „chýba
 * hodnotenie jedného tvrdenia" je adresa, kam sa má hlasujúci vrátiť,
 * zatiaľ čo „škála 0 - 3" v tej chvíli nehovorí nič.
 */
export const answerMeta = (
  question: AnsweredShape,
  answers: AnswerOption[] | undefined,
): string => {
  const value = answers ?? [];

  // Označená možnosť „Iné" bez textu je nedokončená odpoveď, nie odpoveď -
  // rovnako to číta `isQuestionAnswered`, takže by inak karta zožltla a meta
  // riadok nad ňou hlásil, že je všetko v poriadku.
  const freeWithoutText = value.some((answer) => {
    const option = question.options.find(
      (candidate) => candidate.option_id === answer.id,
    );
    return option?.type === 'free' && !answer.text?.trim();
  });

  if (question.type === 'single_choice') {
    if (freeWithoutText) return __('chýba text k vlastnej odpovedi');
    return value.length ? __('jedna možnosť') : __('chýba odpoveď');
  }

  if (question.type === 'free_text') {
    return value.some((answer) => answer.text?.trim())
      ? __('otvorená odpoveď')
      : `${__('otvorená odpoveď')} · ${__('nevyplnené')}`;
  }

  if (question.type === 'multi_choice') {
    if (freeWithoutText) return __('chýba text k vlastnej odpovedi');

    return value.length < question.min_options
      ? `${__('vybrané')} ${value.length}, ${__('treba aspoň')} ${question.min_options}`
      : `${__('vybrané')} ${value.length} ${__('z')} ${question.max_options}`;
  }

  const rated = new Set(
    value
      .filter((answer) => typeof answer.value === 'number')
      .map((answer) => answer.id),
  );
  const missing = question.options.filter(
    (option) => option.required !== false && !rated.has(option.option_id),
  ).length;

  if (missing) {
    return missing === 1
      ? __('chýba hodnotenie jedného tvrdenia')
      : `${__('chýbajú hodnotenia')} ${missing} ${__('tvrdení')}`;
  }

  const points = scalePoints(question);
  const total = question.options.length;
  const range = `${__('škála')} ${points[0]} - ${points[points.length - 1]}`;

  return `${range} · ${__('ohodnotené')} ${rated.size} ${__('z')} ${total} ${declineStatements(total)}`;
};

export const answerLines = (
  question: Omit<AnsweredShape, 'options'> & {
    options: {
      option_id: number;
      type: 'choice' | 'free';
      title: string;
      required?: boolean;
    }[];
  },
  answers: AnswerOption[] | undefined,
): { text: string; filled: boolean }[] => {
  const value = answers ?? [];

  if (question.type === 'scale') {
    return question.options.map((option) => {
      const answer = value.find(
        (candidate) => candidate.id === option.option_id,
      );
      if (answer?.value !== undefined) {
        return { text: `${option.title} - ${answer.value}`, filled: true };
      }
      // Neohodnotené nepovinné tvrdenie **nie je** chýbajúca odpoveď, len sa
      // nehodnotilo - inak by kontrola pred odoslaním žltla nad lístkom, ktorý
      // server prijme.
      return {
        text: `${option.title} - ${__('nehodnotené')}`,
        filled: option.required === false,
      };
    });
  }

  if (!value.length) {
    return [{ text: __('Bez odpovede'), filled: false }];
  }

  return value.map((answer) => {
    const option = question.options.find(
      (candidate) => candidate.option_id === answer.id,
    );

    if (option?.type === 'free' || question.type === 'free_text') {
      const text = answer.text?.trim();
      return text
        ? { text, filled: true }
        : { text: __('Bez odpovede'), filled: false };
    }

    return { text: option?.title ?? '', filled: true };
  });
};

/**
 * Odpovede z formulárového tvaru do tela `submitVote`.
 *
 * Prázdna otázka sa **vynecháva celá** - otvorená odpoveď je nepovinná
 * a poslať ju ako možnosť bez textu by server odmietol.
 */
export const toVotesPayload = (answers: VotingAnswers) =>
  Object.entries(answers)
    .map(([questionId, options]) => ({
      question_id: Number(questionId),
      options: (options ?? [])
        .filter(
          (option) =>
            option.value !== undefined ||
            option.text === undefined ||
            !!option.text.trim(),
        )
        .map((option) => ({
          option_id: option.id,
          ...(option.text?.trim() ? { text: option.text.trim() } : {}),
          ...(option.value !== undefined ? { value: option.value } : {}),
        })),
    }))
    .filter((vote) => vote.options.length > 0);

/** Odovzdaný lístok (`my_votes`) do tvaru, s ktorým pracujú obrazovky. */
export const answersFromMyVotes = (
  myVotes:
    | {
        question_id: number;
        options: { option_id: number; text?: string; value?: number }[];
      }[]
    | undefined,
): VotingAnswers | undefined => {
  if (!myVotes?.length) return undefined;

  const answers: VotingAnswers = {};
  myVotes.forEach(({ question_id, options }) => {
    answers[question_id] = (options ?? []).map((option) => ({
      id: option.option_id,
      questionId: question_id,
      ...(option.text !== undefined && { text: option.text }),
      ...(option.value !== undefined && { value: option.value }),
    }));
  });
  return answers;
};
